import { useId, useState, type ReactNode } from 'react'
import { Link, router, usePage } from '@inertiajs/react'

import cn from '@components/utils/cn'
import Icon from '@components/utils/Icon'

import { COMPONENT_DOCS, TIER_LABEL, TIER_ORDER } from '../docs/registry'

type DocsPageProps = { indexHref: string }

export type RailItem = { label: string; href: string }

export type RailGroup = { label: string; items: RailItem[] }

export function docsNavGroups(base: string): RailGroup[] {
  const foundations: RailItem[] = [
    { label: 'Colour', href: `${base}/foundations` },
    { label: 'Navigation reference', href: `${base}/nav_reference` },
  ]

  return [
    { label: 'Foundations', items: foundations },
    ...TIER_ORDER.map((tier) => ({
      label: TIER_LABEL[tier],
      items: COMPONENT_DOCS.filter((doc) => doc.tier === tier).map((doc) => ({
        label: doc.name,
        href: `${base}/components/${doc.slug}`,
      })),
    })).filter((group) => group.items.length > 0),
  ]
}

export function filterNavGroups(groups: RailGroup[], term: string): RailGroup[] {
  const needle = term.trim().toLowerCase()
  if (!needle) return groups

  return groups
    .map((group) => ({ ...group, items: group.items.filter((item) => item.label.toLowerCase().includes(needle)) }))
    .filter((group) => group.items.length > 0)
}

export function DocsLayout({ children }: { children: ReactNode }) {
  const { props, url } = usePage<DocsPageProps>()
  const base = props.indexHref
  const path = url.split('?')[0]

  const [query, setQuery] = useState('')
  const inputId = useId()

  const groups = filterNavGroups(docsNavGroups(base), query)
  const firstMatch = groups[0]?.items[0]

  return (
    <div className="tw:flex tw:min-h-dvh tw:bg-white">
      <nav
        aria-label="Design docs"
        className="tw:sticky tw:top-0 tw:flex tw:h-dvh tw:w-60 tw:shrink-0 tw:flex-col tw:border-r tw:border-gray-cool-200 tw:px-4 tw:py-5"
      >
        <Link
          href={base}
          className="tw:mb-4 tw:flex tw:items-center tw:gap-2 tw:px-2 tw:text-base tw:font-bold tw:text-primary-600"
        >
          <Icon type="book-open-01" className="tw:h-5 tw:w-5" aria-hidden />
          Design docs
        </Link>

        <form
          role="search"
          className="tw:mb-5"
          onSubmit={(event) => {
            event.preventDefault()
            if (firstMatch) router.visit(firstMatch.href)
          }}
        >
          <label htmlFor={inputId} className="tw:sr-only">
            Search the design docs
          </label>
          <div className="tw:flex tw:items-center tw:gap-2 tw:rounded-md tw:border tw:border-gray-cool-200 tw:bg-white tw:px-2.5 tw:py-1.5 tw:focus-within:border-primary-500 tw:focus-within:ring-2 tw:focus-within:ring-primary-200">
            <Icon type="search-lg" className="tw:h-4 tw:w-4 tw:shrink-0 tw:text-gray-cool-400" aria-hidden />
            <input
              id={inputId}
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search"
              autoComplete="off"
              className="tw:w-full tw:border-0 tw:bg-transparent tw:p-0 tw:text-sm tw:text-gray-cool-900 tw:placeholder:text-gray-cool-400 tw:focus:outline-hidden tw:focus:ring-0"
            />
          </div>
        </form>

        <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:gap-4 tw:overflow-y-auto">
          {groups.length === 0 && (
            <p className="tw:px-2 tw:text-sm tw:text-gray-cool-500">Nothing documented matches that yet.</p>
          )}

          {groups.map((group) => (
            <div key={group.label}>
              <h2 className="tw:mb-1 tw:px-2 tw:text-xs tw:font-bold tw:uppercase tw:tracking-wide tw:text-gray-cool-400">
                {group.label}
              </h2>
              <ul className="tw:flex tw:flex-col">
                {group.items.map((item) => {
                  const active = path === item.href
                  return (
                    <li key={item.href}>
                      <Link
                        href={item.href}
                        aria-current={active ? 'page' : undefined}
                        className={cn(
                          'tw:block tw:rounded-md tw:px-2 tw:py-1.5 tw:text-sm tw:text-gray-cool-600 tw:hover:bg-gray-cool-50 tw:hover:text-gray-cool-900',
                          active &&
                            'tw:bg-primary-50 tw:font-medium tw:text-primary-700 tw:hover:bg-primary-50 tw:hover:text-primary-700'
                        )}
                      >
                        {item.label}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </div>
        <a
          href="/"
          className="tw:flex tw:shrink-0 tw:items-center tw:gap-2 tw:border-t tw:border-gray-cool-200 tw:px-2 tw:pt-4 tw:text-sm tw:font-medium tw:text-gray-cool-600 tw:hover:text-gray-cool-900"
        >
          <Icon type="arrow-narrow-left" className="tw:h-4 tw:w-4" aria-hidden />
          Back to SupporterBase
        </a>
      </nav>

      <main className="tw:min-w-0 tw:flex-1">{children}</main>
    </div>
  )
}

export const docsLayout = (page: ReactNode) => <DocsLayout>{page}</DocsLayout>
